
import React, { useId } from "react";

interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: React.ReactNode;
  description?: React.ReactNode;
  /** Rendert den Radio als umrandete Karte (Default: false). */
  showBorder?: boolean;
  containerClassName?: string;
}

/**
 * Einzelner Design-System-Radio. Natives input[type=radio], token-/theme-aware,
 * native API (forwardRef + input-Props) — controlled oder mit react-hook-form.
 * Für Gruppen bevorzugt {@link RadioGroup} nutzen.
 */
export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, description, showBorder = false, containerClassName = "", className = "", disabled, ...props },
  ref
) {
  const content = (
    <div className={`flex gap-3 ${description ? "items-start" : "items-center"}`}>
      <input
        ref={ref}
        type="radio"
        disabled={disabled}
        className={`ds-control-accent h-4 w-4 border-line-strong bg-surface ${description ? "mt-1" : ""} ${
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        } ${className}`}
        {...props}
      />
      <div className={`flex-1 ${disabled ? "opacity-60" : ""}`}>
        <span className="text-sm font-medium text-content-2">{label}</span>
        {description && <p className="mt-1 text-xs text-content-3">{description}</p>}
      </div>
    </div>
  );

  return (
    <label
      htmlFor={props.id}
      className={
        showBorder
          ? `block rounded-lg border border-line-strong bg-surface px-4 ${
              description ? "pb-0 pt-3" : "py-3"
            } ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${containerClassName}`
          : `flex ${disabled ? "cursor-not-allowed" : "cursor-pointer"} items-center ${containerClassName}`
      }
    >
      {content}
    </label>
  );
});

export interface RadioOption<T extends string> {
  value: T;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}

interface RadioGroupProps<T extends string> {
  value: T | null;
  onChange: (value: T) => void;
  options: RadioOption<T>[];
  /** Gemeinsamer name der Radios; default: generierte ID. */
  name?: string;
  disabled?: boolean;
  showBorder?: boolean;
  /** Container-Klassen (Default: vertikale Liste). */
  className?: string;
}

/**
 * Radio-Gruppe mit exklusiver Auswahl. Ergonomische API analog {@link Segmented}
 * (options/value/onChange), rendert intern {@link Radio}s mit gemeinsamem name.
 */
export function RadioGroup<T extends string>({
  value,
  onChange,
  options,
  name,
  disabled,
  showBorder,
  className = "flex flex-col gap-2",
}: RadioGroupProps<T>) {
  const fallbackName = useId();
  const groupName = name ?? fallbackName;
  return (
    <div role="radiogroup" className={className}>
      {options.map((opt) => (
        <Radio
          key={opt.value}
          name={groupName}
          value={opt.value}
          checked={value === opt.value}
          onChange={(e) => e.target.checked && onChange(opt.value)}
          label={opt.label}
          description={opt.description}
          disabled={disabled || opt.disabled}
          showBorder={showBorder}
        />
      ))}
    </div>
  );
}
